import { useMemo } from 'react';
import { Ambassador } from '@restworld/utility-types';
import {
  Challenge,
  JobPositionPerformanceMetrics,
  ManagedAmbassador,
  ManagedProgramRoster,
  ManagedProgramTeamAggregate,
  ManagedRosterEntry,
  ManagedTeamAggregate,
  Metrics,
} from './types';
import { ENDPOINTS } from '../../endpoints';
import { Service } from '../base';
import dynamicAxios from '../../utils/axios';
import useServices from '../../hooks/useServices';

class ServiceAmbassadors extends Service {
  me() {
    return this.axiosInstance.get<Ambassador>(ENDPOINTS.ambassadors.me);
  }
  getMetrics(fromDate: string, toDate: string, challengeId?: string) {
    return this.axiosInstance.get<Metrics>(ENDPOINTS.ambassadors.metrics, {
      params: { from_date: fromDate, to_date: toDate, challenge_id: challengeId },
    });
  }
  getChallenges() {
    return this.axiosInstance.get<Challenge[]>(ENDPOINTS.ambassadors.challenges);
  }
  acceptTermsAndConditions(userId: string) {
    return this.axiosInstance.get(ENDPOINTS.ambassadors.acceptTermsAndConditions, { params: { user_id: userId } });
  }
  getJobPositionPerformanceMetrics(fromDate: string, toDate: string, challengeId?: string) {
    return this.axiosInstance.get<JobPositionPerformanceMetrics[]>(
      ENDPOINTS.ambassadors.jobPositionPerformanceMetrics,
      { params: { from_date: fromDate, to_date: toDate, challenge_id: challengeId } }
    );
  }
  getManagedChallengers() {
    return this.axiosInstance.get<ManagedAmbassador[]>(ENDPOINTS.ambassadors.managed);
  }
  getManagedTeamAggregate(fromDate?: string, toDate?: string) {
    return this.axiosInstance.get<ManagedTeamAggregate>(ENDPOINTS.ambassadors.managedAggregate, {
      params: { from_date: fromDate, to_date: toDate },
    });
  }
  getManagedRoster(fromDate?: string, toDate?: string) {
    return this.axiosInstance.get<ManagedRosterEntry[]>(ENDPOINTS.ambassadors.managedRoster, {
      params: { from_date: fromDate, to_date: toDate },
    });
  }
  /**
   * Program variant of the team aggregate. `category=program` switches the
   * backend onto the Program rules (monthly window, caps 5/30, no cycle pool);
   * without it the route answers exactly as it does today.
   */
  getManagedProgramTeamAggregate(fromDate?: string, toDate?: string) {
    return this.axiosInstance.get<ManagedProgramTeamAggregate>(ENDPOINTS.ambassadors.managedAggregate, {
      params: { from_date: fromDate, to_date: toDate, category: 'program' },
    });
  }
  /**
   * Program variant of the roster. The response is an object wrapping the rows
   * plus the window the backend used, not a bare array like the challenger one.
   * `from_date` and `to_date` must fall in the same calendar month or the route
   * answers 400: the caller picks a month, never a free range.
   */
  getManagedProgramRoster(fromDate?: string, toDate?: string) {
    return this.axiosInstance.get<ManagedProgramRoster>(ENDPOINTS.ambassadors.managedRoster, {
      params: { from_date: fromDate, to_date: toDate, category: 'program' },
    });
  }
  getManagedMetrics(ambassadorCode: string, fromDate: string, toDate: string, challengeId?: string) {
    return this.axiosInstance.get<Metrics>(ENDPOINTS.ambassadors.managedMetrics(ambassadorCode), {
      params: { from_date: fromDate, to_date: toDate, challenge_id: challengeId },
    });
  }
  getManagedJobPositionPerformanceMetrics(
    ambassadorCode: string,
    fromDate: string,
    toDate: string,
    challengeId?: string
  ) {
    return this.axiosInstance.get<JobPositionPerformanceMetrics[]>(
      ENDPOINTS.ambassadors.managedJobPositionPerformanceMetrics(ambassadorCode),
      { params: { from_date: fromDate, to_date: toDate, challenge_id: challengeId } }
    );
  }
}

export function useAmbassadorService() {
  const { baseUrl, jwt } = useServices();
  return useMemo(() => new ServiceAmbassadors(dynamicAxios(baseUrl, jwt)), [baseUrl, jwt]);
}

export * from './types';
export default useAmbassadorService;
